// Leave Overlay Hooks
// Part of LEAVE_OVERLAY_IMPLEMENTATION.md - Phase 2

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { leaveQueryKeys } from './queryKeys';
import type { LeaveOverlay } from '@/services/leave/leaveOverlayTypes';

/**
 * Fetch approved leave overlays for a company within a date range (manager view)
 */
export const useCompanyLeaveOverlay = (
  companyId: string | undefined,
  startDate: string,
  endDate: string
) => {
  return useQuery({
    queryKey: leaveQueryKeys.companyLeaveOverlay(companyId || '', startDate, endDate),
    queryFn: async (): Promise<LeaveOverlay[]> => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          id,
          profile_id,
          leave_type,
          start_date,
          end_date,
          profiles!inner (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'approved')
        .lte('start_date', endDate)    // Leave starts before or on view end
        .gte('end_date', startDate);   // Leave ends after or on view start
      
      if (error) {
        console.error('[useCompanyLeaveOverlay] Error:', error);
        throw error;
      }
      
      return (data || []).map((item: any) => ({
        leave_request_id: item.id,
        profile_id: item.profile_id,
        profile_name: [item.profiles?.first_name, item.profiles?.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() || '—',
        leave_type: item.leave_type,
        start_date: item.start_date,
        end_date: item.end_date,
      }));
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,    // 5 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Fetch approved leave overlays for a specific employee (employee view)
 */
export const useMyLeaveOverlay = (
  profileId: string | undefined,
  startDate: string,
  endDate: string
) => {
  return useQuery({
    queryKey: leaveQueryKeys.myLeaveOverlay(profileId || '', startDate, endDate),
    queryFn: async (): Promise<LeaveOverlay[]> => {
      if (!profileId) return [];
      
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          id,
          profile_id,
          leave_type,
          start_date,
          end_date,
          profiles!inner (
            first_name,
            last_name
          )
        `)
        .eq('profile_id', profileId)
        .eq('status', 'approved')
        .lte('start_date', endDate)
        .gte('end_date', startDate);
      
      if (error) {
        console.error('[useMyLeaveOverlay] Error:', error);
        throw error;
      }
      
      return (data || []).map((item: any) => ({
        leave_request_id: item.id,
        profile_id: item.profile_id,
        profile_name: [item.profiles?.first_name, item.profiles?.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() || '—',
        leave_type: item.leave_type,
        start_date: item.start_date,
        end_date: item.end_date,
      }));
    },
    enabled: !!profileId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};
